import { useCallback, useEffect, useState, useMemo } from 'react'
import { Activity, CheckCircle, AlertCircle, Clock, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react'

type AnalyticsTimeRange = '7d' | '30d' | '90d'

interface DailyStats {
  date: string
  totalRequests: number
  successfulRequests: number
  failedRequests: number
  averageResponseTime: number
  pointsCharged: number
}

interface ServiceStats {
  service_id: string
  name?: string
  total_requests?: number
  successful_requests?: number
  failed_requests?: number
  average_response_time?: number
  total_points_charged?: number
  points?: number
}

interface AnalyticsData {
  totalRequests: number
  successfulRequests: number
  failedRequests: number
  averageResponseTime: number
  totalPointsCharged: number
  uniqueUsers: number
  serviceBreakdown: ServiceStats[]
  dailyStats: DailyStats[]
}

// API 调用辅助函数
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('admin_token')
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

export default function AnalyticsPage() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [timeRange, setTimeRange] = useState<AnalyticsTimeRange>('7d')
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState<'requests' | 'successRate' | 'responseTime' | 'errors'>('requests')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)
    try {
      const days =
        timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 7

      const result = await apiCall(`/api/admin/analytics?days=${days}`)
      setAnalyticsData(result.data)
    } catch (error) {
      console.error('Failed to load analytics', error)
      alert(error instanceof Error ? error.message : '获取分析数据失败')
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    void fetchAnalytics()
  }, [fetchAnalytics])

  // 计算成功率
  const successRate = useMemo(() => {
    if (!analyticsData || analyticsData.totalRequests === 0) return 0
    return Math.round((analyticsData.successfulRequests / analyticsData.totalRequests) * 100)
  }, [analyticsData])

  // 处理服务数据并排序
  const sortedServices = useMemo(() => {
    if (!analyticsData) return []

    return [...analyticsData.serviceBreakdown]
      .map(service => {
        const totalRequests = service.total_requests || 0
        const successfulRequests = service.successful_requests || 0
        const failedRequests = service.failed_requests || 0
        const avgResponseTime = service.average_response_time || 0
        const successRate = totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0

        return {
          ...service,
          totalRequests,
          successfulRequests,
          failedRequests,
          avgResponseTime,
          successRate
        }
      })
      .sort((a, b) => {
        let compareValue = 0
        switch (sortField) {
          case 'requests':
            compareValue = a.totalRequests - b.totalRequests
            break
          case 'successRate':
            compareValue = a.successRate - b.successRate
            break
          case 'responseTime':
            compareValue = a.avgResponseTime - b.avgResponseTime
            break
          case 'errors':
            compareValue = a.failedRequests - b.failedRequests
            break
        }
        return sortOrder === 'asc' ? compareValue : -compareValue
      })
  }, [analyticsData, sortField, sortOrder])

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  if (loading) {
    return (
      <section className="flex min-h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto animate-spin rounded-full border-4 border-blue-600 border-t-transparent mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </section>
    )
  }

  if (!analyticsData) {
    return (
      <section className="flex min-h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <p className="text-gray-600 mb-4">加载失败</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            重试
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="min-h-screen w-full overflow-auto p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">运维监控</h1>
          <p className="text-gray-600">系统性能分析和问题诊断</p>
        </div>

        {/* 时间范围 + 刷新 */}
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-white rounded-lg border border-gray-200 p-1">
            {(['7d', '30d', '90d'] as AnalyticsTimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  range === timeRange
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
          <button
            onClick={fetchAnalytics}
            className="p-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition"
            title="刷新数据"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* 一级指标：系统性能概览 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <PerformanceCard
          title="总请求量"
          value={analyticsData.totalRequests.toLocaleString()}
          subtitle={`独立用户: ${analyticsData.uniqueUsers}`}
          icon={<Activity className="w-6 h-6" />}
          color="blue"
        />

        <PerformanceCard
          title="成功率"
          value={`${successRate}%`}
          subtitle={`成功: ${analyticsData.successfulRequests.toLocaleString()}`}
          icon={<CheckCircle className="w-6 h-6" />}
          color={successRate >= 95 ? 'green' : successRate >= 90 ? 'yellow' : 'red'}
          status={successRate >= 95 ? 'good' : 'warning'}
        />

        <PerformanceCard
          title="平均响应"
          value={`${analyticsData.averageResponseTime.toFixed(0)}ms`}
          subtitle={analyticsData.averageResponseTime < 1000 ? '性能良好' : '需要优化'}
          icon={<Clock className="w-6 h-6" />}
          color={analyticsData.averageResponseTime < 1000 ? 'green' : 'yellow'}
        />

        <PerformanceCard
          title="失败请求"
          value={analyticsData.failedRequests.toLocaleString()}
          subtitle={`失败率: ${((analyticsData.failedRequests / analyticsData.totalRequests) * 100).toFixed(1)}%`}
          icon={<AlertCircle className="w-6 h-6" />}
          color={analyticsData.failedRequests === 0 ? 'green' : 'red'}
          status={analyticsData.failedRequests > 0 ? 'error' : 'good'}
        />
      </div>

      {/* 二级信息：服务性能表格 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">服务性能分析</h2>
          <p className="text-sm text-gray-500 mt-1">点击列标题排序</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  服务名称
                </th>
                <th
                  className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('requests')}
                >
                  <div className="flex items-center justify-end gap-1">
                    调用次数
                    {sortField === 'requests' && (
                      sortOrder === 'desc' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('successRate')}
                >
                  <div className="flex items-center justify-end gap-1">
                    成功率
                    {sortField === 'successRate' && (
                      sortOrder === 'desc' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('responseTime')}
                >
                  <div className="flex items-center justify-end gap-1">
                    平均响应
                    {sortField === 'responseTime' && (
                      sortOrder === 'desc' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('errors')}
                >
                  <div className="flex items-center justify-end gap-1">
                    错误次数
                    {sortField === 'errors' && (
                      sortOrder === 'desc' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedServices.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    暂无数据
                  </td>
                </tr>
              ) : (
                sortedServices.map((service) => (
                  <tr key={service.service_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{service.name || service.service_id}</div>
                      <div className="text-xs text-gray-500">{service.service_id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium">
                      {service.totalRequests.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        service.successRate >= 95 ? 'bg-green-100 text-green-800' :
                        service.successRate >= 90 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {service.successRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        service.avgResponseTime < 1000 ? 'bg-green-100 text-green-800' :
                        service.avgResponseTime < 3000 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {service.avgResponseTime.toFixed(0)}ms
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {service.failedRequests > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          {service.failedRequests}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 三级信息：每日统计 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">每日统计</h2>
          <p className="text-sm text-gray-500 mt-1">最近 {timeRange} 的请求趋势</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">日期</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">请求数</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">成功</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">失败</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">平均响应</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">消耗积分</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {analyticsData.dailyStats.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    暂无数据
                  </td>
                </tr>
              ) : (
                analyticsData.dailyStats.map((day) => (
                  <tr key={day.date} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-700 font-medium">{day.date}</td>
                    <td className="px-6 py-4 text-right text-gray-900">{day.totalRequests.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right text-green-600 font-medium">{day.successfulRequests.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right text-red-600 font-medium">{day.failedRequests.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right text-gray-900">{day.averageResponseTime.toFixed(0)}ms</td>
                    <td className="px-6 py-4 text-right text-gray-900">{day.pointsCharged.toFixed(0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

// 性能指标卡片
function PerformanceCard({
  title,
  value,
  subtitle,
  icon,
  color = 'blue',
  status
}: {
  title: string
  value: string
  subtitle: string
  icon: React.ReactNode
  color?: 'blue' | 'green' | 'red' | 'yellow'
  status?: 'good' | 'warning' | 'error'
}) {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600',
    yellow: 'from-yellow-500 to-yellow-600',
  }

  const statusIndicator = status ? (
    <div className={`absolute top-4 right-4 w-2 h-2 rounded-full ${
      status === 'good' ? 'bg-green-500' :
      status === 'warning' ? 'bg-yellow-500' :
      'bg-red-500'
    }`} />
  ) : null

  return (
    <div className="relative bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      {statusIndicator}

      {/* 图标和标题 */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-600">{title}</span>
        <div className={`p-2 rounded-lg bg-gradient-to-br ${colorClasses[color]} text-white`}>
          {icon}
        </div>
      </div>

      {/* 数值 */}
      <div className="mb-2">
        <span className="text-3xl font-bold text-gray-900">{value}</span>
      </div>

      {/* 副标题 */}
      <div className="text-sm text-gray-500">{subtitle}</div>
    </div>
  )
}
